// stores/linkStore.ts

import { useClient } from '@/composables/useClient';
import { Link, ReadOnlyProps, User } from '@/services/client/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface LinkState {
  loading: boolean;
  error: string | null;
}

export const useLinkStore = defineStore('link', () => {
  const state = ref<LinkState>({
    loading: false,
    error: null,
  });

  const client = useClient();

  // Actions
  const fetchLinksForUser = async (user: Omit<User, ReadOnlyProps>): Promise<Link[]> => {
    state.value.loading = true;
    state.value.error = null;
    try {
      // Fetch links for a user directly from the backend
      return await client.getUserLinks(user);
    } catch (err) {
      state.value.error = (err as Error).message;
      throw err;
    } finally {
      state.value.loading = false;
    }
  };

  const addLink = async (
    user: Omit<User, ReadOnlyProps>,
    link: Omit<Link, 'id' | 'userId' | ReadOnlyProps>
  ): Promise<Link> => {
    state.value.loading = true;
    state.value.error = null;
    try {
      // Add a link via the API
      console.warn("HI", link.queryArg)
      return await client.addLink(user, link);
    } catch (err) {
      state.value.error = (err as Error).message;
      throw err;
    } finally {
      state.value.loading = false;
    }
  };

  const updateLink = async (user: User, link: Omit<Link, ReadOnlyProps>): Promise<Link> => {
    state.value.loading = true;
    state.value.error = null;
    try {
      // Update the link via the API
      return await client.updateLink(user, link);
    } catch (err) {
      state.value.error = (err as Error).message;
      throw err;
    } finally {
      state.value.loading = false;
    }
  };

  const clearLinks = () => {
    // No links are cached, so this just resets any error or loading state
    state.value.loading = false;
    state.value.error = null;
  };

  return {
    // State
    loading: computed(() => state.value.loading),
    error: computed(() => state.value.error),

    // Actions
    fetchLinksForUser,
    addLink,
    updateLink,
    clearLinks,
  };
}, {
  persist: {
    key: 'link',
    storage: localStorage, // Or sessionStorage
  },
});